import React from "react"
import HomepageData from "./data/extendedhomepage.json"
import Styles from "./css/extendedhomepage.module.scss"
import {isChrome,isFirefox,isIeEdge,isSafari, getBrowser} from "@tightrope/lpscripts/browserdetect"

// Sanitize HTML to prevent XSS attacks - only allows safe formatting tags
const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Allow only safe tags: span, br, strong, em, b, i, u, p, div
  const allowedTags = ['span', 'br', 'strong', 'em', 'b', 'i', 'u', 'p', 'div'];
  const allowedAttributes = ['class', 'style'];
  
  // Parse and sanitize
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Remove dangerous tags and attributes
  const walker = document.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    null
  );
  
  const nodesToRemove = [];
  let node;
  
  while (node = walker.nextNode()) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // Remove disallowed tags
      if (!allowedTags.includes(node.tagName.toLowerCase())) {
        nodesToRemove.push(node);
        continue;
      }
      
      // Remove dangerous attributes
      Array.from(node.attributes).forEach(attr => {
        if (!allowedAttributes.includes(attr.name.toLowerCase())) {
          // Remove dangerous attributes like onclick, onerror, etc.
          if (attr.name.toLowerCase().startsWith('on') || 
              attr.name.toLowerCase() === 'href' || 
              attr.name.toLowerCase() === 'src') {
            node.removeAttribute(attr.name);
          }
        }
      });
    }
  }
  
  // Remove disallowed nodes
  nodesToRemove.forEach(n => n.parentNode && n.parentNode.removeChild(n));
  
  return doc.body.innerHTML;
}

class ExtendedHomepage extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      isChrome: isChrome(),
      isFirefox: isFirefox(),
      isIeEdge: isIeEdge(),
      isSafari: isSafari(),
      display: 'none',
      pageData: props.data,
      data: HomepageData
    }
    this.browserRef = React.createRef();
    this.overlayRef = React.createRef();
    this.bannerRef = React.createRef();
    this.typingTextRef = React.createRef();
    this.typingIndex = 0;
    this.typingIntervals = [];
  }

  headline(data){
    let params = new URLSearchParams(window.location.search);
    let hc = params.get("hc");
    let kw = params.get("kw");
    let bn = params.get("bn");
    let qsData = {};
    if(hc){
      // Sanitize query parameter to prevent XSS
      qsData.headline = sanitizeHTML(hc);
    }
    if(kw){
      // Sanitize query parameter to prevent XSS
      qsData.subhead = sanitizeHTML(kw);
    }
    if(bn){
      // Sanitize query parameter to prevent XSS
      qsData.mainCTA = sanitizeHTML(bn);
    }
    if(qsData){
      const queryData = Object.assign(data, qsData);
      this.setState({data: queryData});
    }
  }

  showBrowsers(browsers){
    let display = false;
    if(getBrowser() === 'ie' && browsers.includes('ie')){
      display=true;
    }
   else if(getBrowser() === 'firefox' && browsers.includes('firefox')){
      display=true;
    }
   else if(getBrowser() === 'chrome' && browsers.includes('chrome')){
      display=true;
    }
   else if(getBrowser() === 'edge' && browsers.includes('edge')){
      display=true;
    }
  else if (getBrowser() === "safari" && browsers.includes("safari")) {
          display = true;
    }
   else if(getBrowser() === 'other' && browsers.includes('other')){
      display=true;
    }
    if(display)
      this.setState({display: 'block'});
    else
      this.setState({display: 'none'});
  }

  type = (word) => {
    if (!this.typingTextRef.current) return;
    
    let i = 0;
    const writing = setInterval(() => {
      if (!this.typingTextRef.current) {
        clearInterval(writing);
        return;
      }
      this.typingTextRef.current.innerHTML += word.charAt(i);
      i++;
      if(i >= word.length){
        clearInterval(writing);
        setTimeout(this.explode, 1000);
      }
    }, 75);
    this.typingIntervals.push(writing);
  }

  explode = () => {
    if (!this.typingTextRef.current || !this.typingTextRef.current.parentElement) return;
    
    const text = this.typingTextRef.current.innerHTML;
    if (!text) return;
    
    // Hide the original text
    this.typingTextRef.current.style.opacity = '0';
    
    // Create 5 explosion pieces
    const directions = [
      { angle: -90, name: 'up' },      // Up
      { angle: -45, name: 'upRight' }, // 45 degrees
      { angle: 0, name: 'right' },     // Right
      { angle: 45, name: 'downRight' }, // 135 degrees (down-right)
      { angle: 90, name: 'down' }      // Down
    ];
    
    const container = this.typingTextRef.current.parentElement;
    const explosionPieces = [];
    
    directions.forEach((dir, index) => {
      const piece = document.createElement('p');
      piece.innerHTML = text;
      const classNameMap = {
        'up': Styles.explodeUp,
        'upRight': Styles.explodeUpRight,
        'right': Styles.explodeRight,
        'downRight': Styles.explodeDownRight,
        'down': Styles.explodeDown
      };
      piece.className = `${Styles.typingText} ${Styles.explosionPiece} ${classNameMap[dir.name] || ''}`;
      piece.style.position = 'absolute';
      piece.style.left = this.typingTextRef.current.offsetLeft + 'px';
      piece.style.top = this.typingTextRef.current.offsetTop + 'px';
      piece.style.margin = '0';
      piece.style.opacity = '1';
      container.appendChild(piece);
      explosionPieces.push(piece);
    });
    
    // After explosion animation completes, clean up and start next phrase
    setTimeout(() => {
      explosionPieces.forEach(piece => {
        if (piece.parentElement) {
          piece.parentElement.removeChild(piece);
        }
      });
      this.typingTextRef.current.innerHTML = '';
      this.typingTextRef.current.style.opacity = '1';
      
      this.typingIndex++;
      if(this.typingIndex >= this.typingWords.length){
        this.typingIndex = 0;
      }
      this.type(this.typingWords[this.typingIndex]);
    }, 800); // Match animation duration
  }

  erase = () => {
    if (!this.typingTextRef.current) return;
    
    const deleting = setInterval(() => {
      if (!this.typingTextRef.current) {
        clearInterval(deleting);
        return;
      }
      this.typingTextRef.current.innerHTML = this.typingTextRef.current.innerHTML.slice(0, -1);
      if(this.typingTextRef.current.innerHTML.length <= 0){
        clearInterval(deleting);
        this.typingIndex++;
        if(this.typingIndex >= this.typingWords.length){
          this.typingIndex = 0;
        }
        this.type(this.typingWords[this.typingIndex]);
      }
    }, 25);
    this.typingIntervals.push(deleting);
  }

  componentDidMount() {
    this.props.data.then(data => {
      const instanceData = Object.assign(this.state.data, data);
      this.setState({ data: instanceData});
      this.state.isChrome = isChrome();
      this.state.isFirefox = isFirefox();
      this.state.isIeEdge = isIeEdge();
      this.state.isSafari = isSafari();

      if(isChrome()){
        const browserData = Object.assign(this.state.data, this.state.data.chrome);
        this.setState({data: browserData});
      }
      if(isFirefox()){
        const browserData = Object.assign(this.state.data, this.state.data.firefox);
        this.setState({data: browserData});
      }
      if (isSafari()) {
          const browserData = Object.assign(this.state.data, this.state.data.safari);
          this.setState({ data: browserData });
      }
      if (isIeEdge()) {
          const browserData = Object.assign(this.state.data, this.state.data.edge);
          this.setState({ data: browserData });
      }
      this.headline(this.state.data);
      if(this.props.browsers){
        this.showBrowsers(this.props.browsers);
      }
      else{
        this.setState({display: 'block'});
      }

      // Set up interval for animation
      this.animationInterval = setInterval(this.triggerAnimation, 5000);

      // Start typing animation
      this.typingWords = ["What is today's forecast?", "I want to learn to code", "Show me some funny cat videos", "Give me some motivational quotes", "What are the top news stories?"];
      this.typingIndex = 0;
      if (this.typingTextRef.current) {
        this.type(this.typingWords[this.typingIndex]);
      }
    });
  }

  componentWillUnmount() {
    // Clean up interval when component unmounts
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
    }
    // Clean up typing animation intervals
    this.typingIntervals.forEach(interval => clearInterval(interval));
    this.typingIntervals = [];
  }

  render() {
    return (
      <div className = {Styles.extendedhomepage}  style={{ display: this.state.display }} >
      <div className = {Styles.allContainer}>
      <header>
      <img className = {Styles.toplogo} src={this.state.data.logo}/>
      <p className = {Styles.productType} dangerouslySetInnerHTML={{ __html: this.state.data.productType }}></p>
      </header>
      <section className={Styles.banner}>
      <div className = {Styles.bannerLeft}>
      <h1 className = {Styles.headline} dangerouslySetInnerHTML={{ __html: this.state.data.productHeadline }}></h1>
      			<h2 dangerouslySetInnerHTML={{ __html: this.state.data.subhead }}></h2>
      			<button onClick = {() => { window.triggerInstall()}} data-cy="cta" className = {`st2 bn_param cws_dl cl1`}><span dangerouslySetInnerHTML={{ __html: this.state.data.mainCTA }}></span></button>
      			{this.props.children}
      </div>
      <div className = {Styles.bannerRight}>
      <span className = {Styles.bannerRightInner}><p ref={this.typingTextRef} className={Styles.typingText}></p></span>	
      </div>
      </section>

      <div className = {Styles.middle}>
      <div className={Styles.contentLeft}>
      <img className = {Styles.optionsImg} src={this.state.data.optionsImg}/>
      </div>
      <div className={Styles.contentRight}>
      <h3 dangerouslySetInnerHTML={{ __html: this.state.data.mid2Headline }}></h3>
        <h4 dangerouslySetInnerHTML={{ __html: this.state.data.mid2Sub1 }}></h4>
        <p dangerouslySetInnerHTML={{ __html: this.state.data.mid2Line1 }}></p>
        <h4 dangerouslySetInnerHTML={{ __html: this.state.data.mid2Sub2 }}></h4>
        <p dangerouslySetInnerHTML={{ __html: this.state.data.mid2Line2 }}></p>
      <h4 dangerouslySetInnerHTML={{ __html: this.state.data.mid2Sub3 }}></h4>
      <p dangerouslySetInnerHTML={{ __html: this.state.data.mid2Line3 }}></p>
      </div>

      </div>

      <div className = {Styles.bottom}>
      		<h3 className = {Styles.headlineMid} dangerouslySetInnerHTML={{ __html: this.state.data.freedom }}></h3>
          <div>
      		<div className = {Styles.bottomTile}>
          <img alt='Choose multiple websites' className = {Styles.removeBanner} src={this.state.data.tileImg1}/>
          <h4 dangerouslySetInnerHTML={{ __html: this.state.data.freedomHead1 }}></h4>
          <p dangerouslySetInnerHTML={{ __html: this.state.data.freedomPara1 }}></p>
          </div>
          <div className = {Styles.bottomTile}>
          <img alt='Personalized setup' className = {Styles.cruncherCounter} src={this.state.data.tileImg2}/>
          <h4 dangerouslySetInnerHTML={{ __html: this.state.data.freedomHead2 }}></h4>
          <p dangerouslySetInnerHTML={{ __html: this.state.data.freedomPara2 }}></p>
          </div>
          <div className = {Styles.bottomTile}>
          <img alt='Lightweight design' className = {Styles.whitelist} src={this.state.data.tileImg3}/>
          <h4 dangerouslySetInnerHTML={{ __html: this.state.data.freedomHead3 }}></h4>
          <p dangerouslySetInnerHTML={{ __html: this.state.data.freedomPara3 }}></p>
          </div>
          </div>
      </div>

      <div className = {Styles.chunkyFooter}>
      
        <div className={Styles.chunkyFooterLeft}>
          <h6 dangerouslySetInnerHTML={{ __html: this.state.data.footerHead }}></h6>
          <p dangerouslySetInnerHTML={{ __html: this.state.data.footerPara }}></p>

        <button onClick = {() =>  { window.triggerInstall()} } data-cy="cta" className = {`st2 bn_param cws_dl cl1`} dangerouslySetInnerHTML={{ __html: this.state.data.mainCTA }}></button>
        <div class="footerDisc">{this.props.children}</div>
        </div>
        <div className={Styles.chunkyFooterRight}>
        <img alt='AllSearch' src={this.state.data.footerLogo} className = {Styles.footerLogo} />
        </div>
        {/* <video className={Styles.howToVideo} loop autoPlay muted><source src={this.state.data.clearLaptop}></source></video> */}
        </div>
      </div>
    </div>
    )
  }
}
export default ExtendedHomepage
